/**
 * Convert milliseconds to frames at a given FPS.
 */
export function msToFrames(ms: number, fps: number): number {
  return Math.round((ms / 1000) * fps)
}

/**
 * Convert frames to milliseconds at a given FPS.
 */
export function framesToMs(frame: number, fps: number): number {
  return (frame / fps) * 1000
}

/**
 * Check if a segment is active at a given frame based on its timeline in/out (ms).
 */
export function isActiveAtFrame(
  timelineIn: number,
  timelineOut: number,
  frame: number,
  fps: number,
): boolean {
  const inFrame = msToFrames(timelineIn, fps)
  const outFrame = msToFrames(timelineOut, fps)
  return frame >= inFrame && frame < outFrame
}

/** A track segment as the compositions see it (timeline in/out ms, optional duration and trimStartMs). */
type Piece = Record<string, any>

/** Everything about a piece but where it sits and its ids, so the two halves of a split compare equal. */
const pieceLook = ({ id, timeline, duration, trimStartMs, ...rest }: Piece) =>
  JSON.stringify({ ...rest, content: rest.content && { ...rest.content, id: undefined } })

/**
 * `segments` with the pieces of a split joined back where nothing changed between them: one ends
 * where the next starts and the next carries on its media from there. A joined piece plays as one
 * media element, so playback never stops at the seam to load a new one.
 */
export function joinSplitPieces<T extends Piece>(segments: T[]): T[] {
  const joined: T[] = []
  for (const segment of segments) {
    const last = joined[joined.length - 1]
    if (
      last &&
      last.timeline.out === segment.timeline.in &&
      (last.trimStartMs ?? 0) + last.timeline.out - last.timeline.in === (segment.trimStartMs ?? 0) &&
      pieceLook(last) === pieceLook(segment)
    ) {
      joined[joined.length - 1] = {
        ...last,
        timeline: { ...last.timeline, out: segment.timeline.out },
        ...(last.duration && segment.duration && { duration: { ...last.duration, eO: segment.duration.eO } }),
      }
    } else joined.push(segment)
  }
  return joined
}
